<template>
    <b-row style="margin: 0">
        <b-col
            class="d-none d-md-block"
            md="5"
            style="background-color: black; height: 100vh; color: white; padding-top: 64px; padding-left: 50px"
        >
            <h2>Join Our Network</h2>
            <p>Build your talent profile and be seen<br />by decision makers within top companies.</p>
            <div style="position: absolute; bottom: -6px; right: 0px">
                <img style="border-top-left-radius: 110%" src="/images/join_our_network.jpg" alt="Image" />
            </div>
        </b-col>

        <MobileHeader
            class="d-block d-md-none"
            back-to-link="https://talentinfusion.io/"
            header="Join Our Network"
            subheader="Build your talent profile and be seen by decision makers within top companies."
        />

        <b-col class="page-content" sm="12" md="7">
            <b-container class="p-0">
                <b-row class="back-link-wrapper">
                    <b-col style="font-size: 14px; font-weight: 400">
                        <a class="back-link" href="https://talentinfusion.io/">Back</a>
                    </b-col>

                    <b-col cols="5" class="ml-auto" style="text-align: right; font-size: 14px; font-weight: 400">
                        Already have an account?
                        <router-link :to="{ name: $routes.TALENT_LOGIN.name }">Log in</router-link>
                    </b-col>
                </b-row>

                <img
                    class="d-none d-md-block mt-5 mb-4 mx-auto"
                    src="/images/logo/infusion.svg"
                    alt="TalentInfusion"
                    title="TalentInfusion"
                />

                <b-row class="justify-content-center m-0">
                    <h6 class="step-title">Tell us about yourself</h6>
                </b-row>

                <b-row class="mt-0 justify-content-md-center">
                    <b-col sm="10" lg="6">
                        <form @submit.prevent="submitNameForm">
                            <text-field
                                id="first_name"
                                v-model="user.first_name"
                                label="What's your first name?"
                                class="mb-3"
                                required
                            />
                            <text-field
                                id="last_name"
                                v-model="user.last_name"
                                label="What's your last name?"
                                class="mb-3"
                                required
                            />
                            <text-field
                                id="email"
                                v-model="user.talent_email"
                                label="What's your email address?"
                                class="mb-3"
                                required
                            />
                            <SearchSelect
                                id="location"
                                label="Where are you located?"
                                placeholder="City, State/Province, Country"
                                :search-results="locationSearchResults"
                                :add-tag="addJobLocation"
                                :remove-tag="removeJobLocation"
                                :tags="user.location ? [user.location] : []"
                                :loading="locationSearchResultsLoading"
                                :required="true"
                                :hide-tags="true"
                                :default-text="user.location"
                                @fetch-results="fetchLocations"
                            />
                            <!-- Render the reCAPTCHA checkbox -->
                            <div
                                id="onboarding-recaptcha"
                                ref="grecaptcha"
                                class="g-recaptcha"
                                :data-sitekey="recaptchaSiteKey"
                            ></div>
                            <br />
                            <styled-button :disabled="!recaptchaCompleted" class="text-dark w-100" type="submit">
                                <!-- Save the user details and progress to the LinkedIn URL page -->
                                Continue
                            </styled-button>
                            <div>
                                <p style="font-size: 12px; text-align: center" class="body-2">
                                    By signing up, I accept the Talent Infusion
                                    <a href="https://talentinfusion.io/terms-of-service/" target="_bland" rel="noopener"
                                        ><strong>Terms and Conditions</strong></a
                                    >
                                    and
                                    <a href="https://blavity.com/privacy" target="_bland" rel="noopener"
                                        ><strong>Privacy Policy</strong></a
                                    >.
                                </p>
                            </div>
                        </form>
                    </b-col>
                </b-row>
            </b-container>
        </b-col>
    </b-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { LOCATIONS } from '@/api/endpoints';
import SearchSelect from '@/components/Input/SearchSelect/index.vue';
import StyledButton from '@/components/StyledButton.vue';
import TextField from '@/components/TextField.vue';
import { ONBOARDING_UPLOAD_RESUME } from '@/router/routes';
import { activeNamespaces } from '@/store';

import MobileHeader from './sections/MobileHeader.vue';

export default {
    name: 'OnboardingSignup',
    components: {
        TextField,
        StyledButton,
        SearchSelect,
        MobileHeader,
    },
    data() {
        return {
            recaptchaRendered: false,
            recaptchaSiteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
            loading: false,
            recaptchaCompleted: false,
            user: {
                first_name: this.$route.query?.first_name,
                last_name: this.$route.query?.last_name,
                talent_email: this.$route.query?.talent_email ? decodeURIComponent(this.$route.query.talent_email) : '',
                location: this.$route.query?.location,
            },
            errorMessages: {
                first_name: [],
                password: [],
                talent_email: [],
            },
            messages: {
                first_name: [],
                password: [],
                talent_email: [],
            },
            locationSearchResults: [],
            locationSearchResultsLoading: false,
        };
    },
    computed: {
        ...mapGetters(activeNamespaces.app, {
            placesAPISessionToken: 'placesAPISessionToken',
        }),
    },
    created() {
        this.selectedLocations = [this.user.location];
    },
    mounted() {
        window.ReCaptchaLoaded = this.loaded;
        /**
         * Set Recaptcha script in header
         */
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?onload=ReCaptchaLoaded&render=explicit';
        document.head.appendChild(script);
    },
    methods: {
        ...mapActions(activeNamespaces.app, {
            generatePlacesAPISessionToken: 'GENERATE_PLACES_API_SESSION_TOKEN',
        }),
        addJobLocation(tag) {
            this.user.location = tag.title;
        },
        removeJobLocation() {
            this.user.location = '';
        },
        fetchLocations(query) {
            if (!this.placesAPISessionToken) {
                this.generatePlacesAPISessionToken();
            }

            this.locationSearchResultsLoading = true;

            const includesPostalCode = query.split(' ').some((searchToken) => {
                return /^\d+$/.test(searchToken);
            });

            const params = {
                query,
                types: includesPostalCode ? 'postal_code' : '(cities)', // only search for cities
                sessiontoken: this.placesAPISessionToken,
            };

            if (this.isResidentOfUs) {
                params.components = 'country:us';
            } // only restrict to US if the user is US resident

            this.$api
                .get(LOCATIONS.PREDICTIONS(), { params })
                .then(({ data }) => {
                    this.locationSearchResults = data;
                })
                .catch((err) => {
                    this.$console.error(err, 'places API Err');
                })
                .finally(() => {
                    this.locationSearchResultsLoading = false;
                });
        },
        loaded() {
            window.grecaptcha.render(this.$refs.grecaptcha, {
                sitekey: this.recaptchaSiteKey,
                callback: (response) => {
                    if (response) {
                        this.recaptchaCompleted = true;
                    }
                },
            });
        },
        submitNameForm() {
            const query = {
                first_name: this.user.first_name,
                last_name: this.user.last_name,
                location: this.user.location,
                talent_email: encodeURI(this.user.talent_email),
            };
            if (this.$route.query?.referral) {
                query.referral = this.$route.query?.referral;
            }
            this.$router.push({
                name: ONBOARDING_UPLOAD_RESUME.name,
                query,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.back-link-wrapper {
    padding: 16px;
}

.page-content {
    background: #fff;
}

.back-link,
.back-link:hover,
.back-link:hover::before {
    text-decoration: none;
}

.back-link::before {
    content: '\2190';
    /* Unicode character for left arrow */
    margin-right: 5px;
}

.step-title {
    color: #000;
    font-family: Inter;
    font-size: 24px;
    font-weight: 600;
    line-height: 150%; /* 36px */
    margin-bottom: 40px;
}

#onboarding-recaptcha {
    display: flex;
    justify-content: center;
}
</style>
